import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { PREFIX } from "../../constants/labels";

import {
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged
} from "firebase/auth";
import {userManagementAuth} from "../../firebaseConfig";


type User = {
  id: string | undefined;
  email: string | undefined;
  role: string | undefined;
  name: string | undefined;
};

export type Credentials = {
  email: string;
  password: string;
};

type State = {
  user: User | null;
  isLoading: boolean;
  isCheckingSession: boolean;
};

const initialState = {
  user: null,
  isLoading: false,
  isCheckingSession: false,
};

type FetchUserPayload = {
  credential: Credentials;
  errorHandler: () => void;
};

export const fetchUser = createAsyncThunk(
  "user/fetchUser",
  async ({ credential, errorHandler }: FetchUserPayload) => {
    try {
      const firebaseResult = await signInWithEmailAndPassword(
        userManagementAuth,
        credential.email,
        credential.password,
      );
      const idToken = await firebaseResult.user.getIdToken();
      const checkEmailValid = await axios.get(
        `${PREFIX}/api/admin/check-email?email=${credential.email}`,
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      if (checkEmailValid.data.isAvailable) {
        await axios.post(
          `${PREFIX}/api/admin/logIn`,
          {},
          { headers: { Authorization: `Bearer ${idToken}` } },
        );
        return checkEmailValid;
      }
      throw new Error("Email is not valid");
    } catch (err) {
      errorHandler && errorHandler();
      console.error("Failed to log in", err);
      throw err;
    }
  }
);

export const logOut = createAsyncThunk("user/logOut", async () => {
  try {
    await signOut(userManagementAuth);
    await axios.post(`${PREFIX}/api/admin/logOut`);
  } catch (err) {
    console.error("Failed to log out", err);
  }
});

export const checkSession = createAsyncThunk("user/checkSession", async () => {
    try {
        // Wait for Firebase to initialize
        await new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(userManagementAuth, (user) => {
                unsubscribe();
                resolve(user);
            });
        });

        const user = userManagementAuth.currentUser;
        if (user) {
            const idToken = await user.getIdToken();
            await axios.post(
                `${PREFIX}/api/admin/logIn`,
                {},
                { headers: { Authorization: `Bearer ${idToken}` } },
            );
            const checkEmailValid = await axios.get(
                `${PREFIX}/api/admin/check-email?email=${user.email}`,
                { headers: { Authorization: `Bearer ${idToken}` } },
            );
            return checkEmailValid;
        }
        return null;
    } catch (err) {
        console.error("Failed to check session", err);
        throw err;
    }
});
export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: {
    [fetchUser.pending.toString()]: (state: State) => {
      state.isLoading = true;
    },
    [fetchUser.fulfilled.toString()]: (state: State, action) => {
      state.isLoading = false;
      state.user = {
        id: userManagementAuth.currentUser?.uid,
        email: userManagementAuth.currentUser?.email ?? '',
        role: action?.payload?.data.role,
        name: action?.payload?.data.name,
      };
    },
    [fetchUser.rejected.toString()]: (state, action) => {
      state.isLoading = false;
      console.log("Error", action.payload);
    },
    [checkSession.pending.toString()]: (state: State) => {
      state.isCheckingSession = true;
    },
    [checkSession.fulfilled.toString()]: (state: State, action) => {
      state.isCheckingSession = false;
      const user = action.payload;
      if (user) {
        state.user = {
          id: userManagementAuth.currentUser?.uid,
          email: userManagementAuth.currentUser?.email ?? '',
          role: action?.payload?.data.role,
          name: action?.payload?.data.name,
        };
      }
    },
    [checkSession.rejected.toString()]: (state, action) => {
      state.isCheckingSession = false;
      console.log("Error", action.payload);
    },
    [logOut.pending.toString()]: (state: State) => {
      state.isCheckingSession = true;
    },
    [logOut.fulfilled.toString()]: (state: State, action) => {
      state.isCheckingSession = false;
      state.user = null;
    },
    [logOut.rejected.toString()]: (state, action) => {
      state.isCheckingSession = false;
      console.log("Error", action.payload);
    },
  },
});

export default userSlice.reducer;
